import {
  type ApiObject,
  type ApiOptions,
  type IdentifyTraits,
  RudderAnalytics,
} from '@rudderstack/analytics-js'

const ENV_KEYS = {
  enabled: 'VITE_RUDDERSTACK_API_ENABLED',
  writeKey: 'VITE_RUDDERSTACK_FE_WRITE_KEY',
  dataPlaneUrl: 'VITE_RUDDERSTACK_DATA_PLANE_URL',
} as const

const META_KEYS = {
  enabled: 'analytics-enabled',
  writeKey: 'rudderstack-fe-write-key',
  dataPlaneUrl: 'rudderstack-data-plane-url',
  userId: 'analytics-user-id',
} as const

type AnalyticsInstance = RudderAnalytics

type AnalyticsConfig = {
  enabled: boolean
  writeKey?: string
  dataPlaneUrl?: string
}

export type AnalyticsEventProperties = ApiObject

export type PageOptions = {
  pageKey?: string
}

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined'

const isAnalyticsInstance = (
  analytics: Window['rudderanalytics']
): analytics is AnalyticsInstance => analytics instanceof RudderAnalytics

const getAnalytics = (): AnalyticsInstance | undefined => {
  if (!isBrowser()) return undefined

  const analytics = window.rudderanalytics
  if (isAnalyticsInstance(analytics)) return analytics

  return undefined
}

const asTrimmedString = (value: unknown): string | undefined => {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value !== 'string') return undefined

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : undefined
}

const envValue = (key: string): string | undefined => {
  const value = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env?.[key]
  return asTrimmedString(value)
}

const metaValue = (name: string): string | undefined => {
  if (!isBrowser()) return undefined

  // Meta tags are rendered by Rails layouts, so they are fallback config for Rails pages.
  return asTrimmedString(document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content)
}

const configValue = (envKey: string, metaKey: string): string | undefined => {
  return envValue(envKey) ?? metaValue(metaKey)
}

const resolveConfig = (): AnalyticsConfig => {
  return {
    enabled: configValue(ENV_KEYS.enabled, META_KEYS.enabled) === 'true',
    writeKey: configValue(ENV_KEYS.writeKey, META_KEYS.writeKey),
    dataPlaneUrl: configValue(ENV_KEYS.dataPlaneUrl, META_KEYS.dataPlaneUrl),
  }
}

/**
 * Initializes the browser analytics SDK.
 *
 * Reads configuration from Vite env first, then Rails-rendered meta tags,
 * and resolves once the SDK is ready to accept events.
 *
 * @returns A promise that resolves after SDK load completes (or immediately
 * when analytics is disabled/unavailable).
 */
export const initialize = (): Promise<void> => {
  if (!isBrowser()) return Promise.resolve()

  const config = resolveConfig()
  if (!config.enabled) return Promise.resolve()

  const { writeKey, dataPlaneUrl } = config
  if (!writeKey || !dataPlaneUrl) return Promise.resolve()
  if (getAnalytics()) return Promise.resolve()

  const analytics = new RudderAnalytics()

  return new Promise((resolve) => {
    analytics.load(writeKey, dataPlaneUrl, {
      autoTrack: { enabled: true },
      integrations: {
        All: false,
        // events captured in the web-app are primarily for product and UX analysis, so we'll only send to Mixpanel
        Mixpanel: true,
        // the marketing team needs GA4 events to measure campaign performance
        'Google Analytics 4 (GA4)': true,
      },
      useBeacon: true,
      useGlobalIntegrationsConfigInEvents: true,
      onLoaded: () => {
        // this auto identifying after load is only relevant for the Rails pages
        const userId = metaValue(META_KEYS.userId) ?? ''
        if (userId !== '') {
          analytics.identify(userId)
        }

        resolve()
      },
    })
  })
}

/**
 * Associates subsequent events with a known user identity.
 *
 * @param userId - Stable user identifier.
 * @param traits - Optional user traits to attach to the identity.
 */
export const identify = (userId: string, traits?: IdentifyTraits) => {
  getAnalytics()?.identify(userId, traits ?? null)
}

// Use this to reset a session
export const reset = () => {
  getAnalytics()?.reset()
}

/**
 * Captures a named analytics event.
 *
 * @param name - Event name.
 * @param properties - Optional event properties.
 */
export const track = (name: string, properties: AnalyticsEventProperties = {}) => {
  const analytics = getAnalytics()
  if (!analytics) return

  analytics.track(name, properties)
}

/**
 * Records a page view event.
 *
 * Use this on route/page transitions.
 *
 * @param name - Optional page name.
 * @param options - Optional API options for destination-specific routing.
 */
export const page = (name?: string, options: { apiOptions?: ApiOptions } = {}) => {
  const analytics = getAnalytics()
  if (!analytics) return

  // identify, if necessary
  // sort of a bandaid for the Rails pages with turbo navigation
  const unidentified = (analytics.getUserId() ?? '') === ''
  if (unidentified) {
    const userId = metaValue(META_KEYS.userId) ?? ''
    if (userId !== '') {
      analytics.identify(userId)
    }
  }

  analytics.page({ name }, options.apiOptions)
}
